import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const formatPrice = (value) => {
  const numeric = String(value || "").replace(/[^\d]/g, "");
  if (!numeric) return "";
  return "₦" + Number(numeric).toLocaleString();
};

const parsePrice = (value = "") => Number(String(value).replace(/[^\d]/g, ""));

const CostingPage = () => {
  const { requestId } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [drugs, setDrugs] = useState([]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  /* ---------------- FETCH ---------------- */
  const fetchRequest = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${requestId}`
      );

      const nextRequest = res.data?.request;

      if (!nextRequest) {
        setRequest(null);
        setDrugs([]);
        setError("This medication costing request was not found.");
        return;
      }

      const requestDrugs = Array.isArray(nextRequest.drugs)
        ? nextRequest.drugs
        : [];

      setRequest(nextRequest);
      setDrugs(
        requestDrugs.map((d) => ({
          ...d,
          name: d?.name || "Medication",
          quantity: d?.quantity || "",
          price: d?.price ? formatPrice(d.price.toString()) : "",
        }))
      );

      if (!requestDrugs.length) {
        setError("No medicines were found on this costing request.");
      }
    } catch (err) {
      console.error(err);
      setRequest(null);
      setDrugs([]);
      setError(
        err.response?.data?.message ||
          "We could not load this medication costing request."
      );
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  /* ---------------- TOTAL ---------------- */
  const total = drugs.reduce((sum, d) => sum + parsePrice(d.price || "0"), 0);

  /* ---------------- SUBMIT ---------------- */
  const submitCosting = async () => {
    if (!drugs.length) {
      setError("Add at least one medicine price before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${requestId}/cost`,
        {
          drugs: drugs.map((d) => ({
            name: d.name,
            quantity: d.quantity,
            price: parsePrice(d.price),
          })),
        }
      );

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || "Failed to submit medication costing."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p>Loading request...</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F7F8F0] px-4 text-center">
        <div className="max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-[#1E0E62]">
            Request unavailable
          </h1>
          <p className="mt-2 text-sm leading-6 text-gray-500">
            {error || "Request not found or has been closed."}
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="h-screen flex flex-col items-center justify-center text-center px-6">
        <h2 className="text-xl font-semibold text-[#1E0E62]">
          Cost Submitted ✅
        </h2>
        <p className="text-sm text-gray-500 mt-2">
          The doctor and patient have been notified.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8F0] flex justify-center px-4 py-6">
      <div className="w-full max-w-md space-y-6">
        {/* HEADER */}
        <div className="mb-6 text-center">
  {/* TITLE */}
  <h1 className="text-2xl font-semibold text-[#1E0E62] tracking-tight">
    💊 Prescription Costing
  </h1>

  <p className="text-sm text-gray-500 mt-1">
    Review and enter medication pricing
  </p>

  {/* PHARMACY CARD */}
  <div className="mt-5 max-w-md mx-auto bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
    
    {/* NAME */}
    <p className="text-base font-semibold text-[#1E0E62]">
      {request?.pharmacyId?.name || "Pharmacy"}
    </p>

    {/* ADDRESS */}
    {request?.pharmacyId?.location?.address && (
      <p className="text-xs text-gray-400 mt-1 leading-relaxed">
        {request.pharmacyId.location.address}
      </p>
    )}

    {/* DIVIDER */}
    <div className="h-px bg-gray-100 my-3" />

    {/* CONTACTS */}
    <div className="flex items-center justify-center gap-3 flex-wrap">
      
      {request?.pharmacyId?.phoneNumber && (
        <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full">
          <span>📞</span>
          <span>{request.pharmacyId.phoneNumber}</span>
        </div>
      )}

      {request?.pharmacyId?.whatsappNumber && (
        <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-3 py-1.5 rounded-full">
          <span>💬</span>
          <span>{request.pharmacyId.whatsappNumber}</span>
        </div>
      )}

    </div>
  </div>

  {/* FOOT NOTE */}
  <p className="text-xs text-gray-400 mt-4">
    Enter price for each medication below
  </p>
</div>

        {/* DRUG LIST */}
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-4">
          {!drugs.length ? (
            <p className="py-4 text-center text-sm text-gray-500">
              No medicines were found on this request.
            </p>
          ) : (
            drugs.map((d, i) => (
              <div key={i} className="space-y-2">
                <div className="flex justify-between items-center gap-3">
                  <p className="text-sm font-medium text-[#1E0E62]">{d.name}</p>
                  {d.quantity && (
                    <span className="shrink-0 text-xs text-gray-400">
                      {d.quantity}
                    </span>
                  )}
                </div>

                <input
                  placeholder="Enter price"
                  value={d.price}
                  onChange={(e) => {
                    const copy = [...drugs];
                    copy[i].price = formatPrice(e.target.value);
                    setDrugs(copy);
                  }}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25DAC5]"
                />
              </div>
            ))
          )}
        </div>

        {error && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800">
            {error}
          </div>
        )}

        {/* TOTAL */}
        <div className="bg-white rounded-2xl p-4 flex justify-between items-center shadow-sm">
          <span className="text-sm text-gray-500">Total</span>
          <span className="text-lg font-semibold text-[#1E0E62]">
            ₦{total.toLocaleString()}
          </span>
        </div>

        {/* SUBMIT */}
        <button
          onClick={submitCosting}
          disabled={submitting || !drugs.length}
          className="w-full bg-gradient-to-r from-[#25DAC5] to-[#1fbfae] text-white py-3 rounded-xl font-semibold shadow disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Submitting..." : "Submit Cost"}
        </button>
      </div>
    </div>
  );
};

export default CostingPage;
